import React, { useState } from 'react';
import {
	cutLyricsLabel,
	cutLyricsOf,
	driveLinkOf,
	groupStaffFilesByLength,
	isPlayable,
	isSheet,
	staffTrackLabel,
	trackKey,
} from '../../../../services/studentTracksModel';
import RoleButtons from '../../../../components/Tracks/RoleButtons';
import AddTrack from '../../../../components/Tracks/AddTrack';
import TracksOnFile from '../../../../components/Tracks/TracksOnFile';
import TrackEditor, { EditTracksButton } from '../../../../components/Tracks/TrackEditor';
import JobLines from '../tracks/JobLines';
import SectionJumpRow from '../tracks/SectionJumpRow';
import TrimControl from '../tracks/TrimControl';
import SongKeyLine from '../tracks/SongKeyLine';
import { markKeyFor } from '../tracks/sectionMarkers';
import { newFileKeys } from '../tracks/trackStripModel';
import { cutSetFor, visibleJobs } from '../../../../services/trackJobsModel';
import './TracksPane.css';

/**
 * Tracks mode of the Song panel (Lesson Dashboard pass 3 F1, 2026-09-28): the
 * song's CATALOGUE, where the strip above the notes is its live transport.
 * In order:
 *   1. **Working now** — only while a job is worth a line (JobLines).
 *   1b. **Playing track** (2026-10-29, moved from the strip when it became
 *      one line): the setup tools for this song's open track. **Sections**
 *      (jump, loop, Adjust to set a time by ear), **Trim** / Save as a cut,
 *      and the song's key (Find / Confirm). They are used while setting a
 *      song up, not while singing, so they live here; with nothing of this
 *      song open, one line says where they will appear, so they are findable.
 *   2. **On this song** — a row per cut: play, Drive, **Use for recital**,
 *      **Use for audition**; each Length row carries **Cut words** (F5: the
 *      pill used to say "Lyrics", which is also the Song panel's first mode).
 *      A recital / audition write that fails says so here, with Dismiss.
 *   3. **Add a track** — the stepped panel (AddTrack).
 *   4. **Tracks on file · N** — the student's filed cuts no board song
 *      claims, as a labelled disclosure (B2, Anthony 2026-09-27: "Lets try
 *      it"). Open by default when this song has no cuts of its own, so the
 *      one place they live is never a closed box on an empty page.
 * With no student linked the marks are hidden and one line says why.
 */
const TracksPane = ({
	song,
	files = [],
	player,
	roles = null,
	roleError = '',
	onDismissRoleError = null,
	hasStudent = false,
	jobs = [],
	anyMacOn = true,
	dismissed = new Set(),
	onDismissJob = null,
	onRetryJob = null,
	onStopJob = null,
	stopAsk = null,
	add = null, // AddTrack props, or null with no student
	tracksOnFile = [],
	nowMs = null,
	// Admins only (2026-09-29): { docs (this song's pointer docs), allDocs,
	// uid, onChanged }. Null for a teacher, who sees no edit control at all.
	edit = null,
	// The Playing track tools (moved from the strip, 2026-10-29).
	onSetSectionMark = null,
	onSaveTrim = null,
	onSaveCut = null,
	onFindKey = null,
	onConfirmKey = null,
}) => {
	const [openWords, setOpenWords] = useState(null);
	const [editing, setEditing] = useState(false);
	const [fileOpen, setFileOpen] = useState(null); // null = the default below
	const rows = groupStaffFilesByLength(files);
	const working = visibleJobs(jobs, nowMs || Date.now(), dismissed).length > 0;
	const onFileCount = tracksOnFile.reduce((n, t) => n + (t.files ? t.files.length : 0), 0);
	const tracksOnFileOpen = fileOpen == null ? rows.length === 0 : fileOpen;
	const fresh = newFileKeys(files, jobs, nowMs || Date.now());
	// This song's own audio track, open in the player (an embed has no clock).
	const cur = player && player.current;
	const loaded = cur && cur.engine === 'audio' ? files.find((f) => trackKey(f) === cur.key) || null : null;

	return (
		<div className="ld-trackspane">
			{working && (
				<section className="ld-trackspane__sec" aria-label="Working now">
					<h4 className="ld-trackspane__head">Working now</h4>
					<JobLines
						jobs={jobs}
						anyMacOn={anyMacOn}
						nowMs={nowMs}
						dismissed={dismissed}
						onDismiss={onDismissJob}
						onRetry={onRetryJob}
						onStop={onStopJob}
						askFor={stopAsk}
					/>
				</section>
			)}

			{rows.length > 0 && (
				<section className="ld-trackspane__sec ld-trackspane__playing" aria-label="Playing track">
					<h4 className="ld-trackspane__head">Playing track{loaded ? ` · ${staffTrackLabel(loaded)}` : ''}</h4>
					{loaded ? (
						<>
							<SectionJumpRow song={song} file={loaded} files={files} player={player} onSetMark={onSetSectionMark} />
							<TrimControl
								file={loaded}
								player={player}
								trim={(song && song.trims && song.trims[markKeyFor(loaded, files)]) || null}
								onSave={onSaveTrim}
								onSaveCut={onSaveCut}
								cutWhat={cutSetFor(loaded, files)
									.map((f) => f.variant || 'Track')
									.join(', ')}
							/>
							<SongKeyLine file={loaded} files={files} player={player} jobs={jobs} onFind={onFindKey} onConfirm={onConfirmKey} />
						</>
					) : (
						<p className="ld-trackspane__muted">Play one of this song's tracks to set its sections, trim it, or find its key.</p>
					)}
				</section>
			)}

			<section className="ld-trackspane__sec" aria-label="On this song">
				<div className="ld-trackspane__headrow">
					<h4 className="ld-trackspane__head">On this song</h4>
					{edit && edit.docs && edit.docs.length > 0 && (
						<EditTracksButton open={editing} onClick={() => setEditing(!editing)} />
					)}
				</div>
				{edit && editing && edit.docs && edit.docs.length > 0 && (
					<TrackEditor docs={edit.docs} uid={edit.uid} onChanged={edit.onChanged} onClose={() => setEditing(false)} />
				)}
				{rows.length === 0 ? (
					<p className="ld-trackspane__muted">No tracks for {song?.name || 'this song'} yet.</p>
				) : (
					rows.map((row) => {
						const words = cutLyricsOf(row.files);
						const wordsOpen = Boolean(words) && openWords === row.length;
						return (
							<div className="ld-trackspane__group" key={row.length}>
								<div className="ld-trackspane__grouphead">
									<span className="ld-trackspane__length">{row.length}</span>
									{words && (
										<button
											type="button"
											className={`ld-trackspane__words${wordsOpen ? ' is-on' : ''}`}
											aria-pressed={wordsOpen}
											aria-label={`${wordsOpen ? 'Hide' : 'Show'} the words of this cut`}
											onClick={() => setOpenWords(wordsOpen ? null : row.length)}>
											<i className="fa-sharp fa-solid fa-align-left" aria-hidden="true" />
											Cut words
										</button>
									)}
								</div>
								{wordsOpen && (
									<div className={`tp__lyrics${words.unverified ? ' tp__lyrics--unverified' : ''}`} data-testid="track-lyrics">
										<p className="tp__lyricslabel">
											<i className={`fa-sharp fa-solid ${words.unverified ? 'fa-triangle-exclamation' : 'fa-circle-check'}`} aria-hidden="true" />
											{cutLyricsLabel(words)}
										</p>
										{words.lines.map((line, i) => (
											<p className="tp__lyricline" key={i}>
												{line}
											</p>
										))}
									</div>
								)}
								<ul className="ld-trackspane__cuts">
									{row.files.map((f) => {
										const key = trackKey(f);
										const playable = isPlayable(f);
										const isCur = playable && player && player.isCurrent(f);
										const playing = isCur && player.current.engine === 'audio' && player.isPlaying;
										const drive = driveLinkOf(f);
										return (
											<li key={key} className={`ld-trackspane__cut${isCur ? ' is-current' : ''}`}>
												{playable ? (
													<button
														type="button"
														className="ld-trackspane__play"
														aria-label={`${playing ? 'Pause' : 'Play'} ${staffTrackLabel(f)}`}
														onClick={() => player && player.toggle(f)}>
														<i className={`fa-sharp fa-solid ${playing ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" />
													</button>
												) : (
													<i
														className={`fa-sharp fa-solid ${isSheet(f) ? 'fa-file-pdf' : 'fa-file-music'} ld-trackspane__fileicon`}
														aria-hidden="true"
													/>
												)}
												<span className="ld-trackspane__label" title={f.name || staffTrackLabel(f)}>
													{staffTrackLabel(f)}
												</span>
												{fresh.has(key) && <span className="ld-trackspane__new">New</span>}
												{drive && (
													<a className="ld-trackspane__drive" href={drive} target="_blank" rel="noopener noreferrer">
														<i className="fa-sharp fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
														Drive
													</a>
												)}
												{hasStudent && roles && playable && (
													<span className="ld-trackspane__roles">
														<RoleButtons file={f} roles={roles} />
													</span>
												)}
											</li>
										);
									})}
								</ul>
							</div>
						);
					})
				)}
				{!hasStudent && rows.length > 0 && (
					<p className="ld-trackspane__muted">Link a student to save keys and marks.</p>
				)}
				{roleError && (
					<div className="ld-dismissable-error" role="alert">
						<p className="ld-trackspane__err">
							The recital or audition mark could not be saved. Please tell Anthony right away.
							<span className="ld-trackspane__detail">{roleError}</span>
						</p>
						{onDismissRoleError && (
							<button type="button" className="ld-dismiss" onClick={onDismissRoleError}>
								Dismiss
							</button>
						)}
					</div>
				)}
			</section>

			{add && (
				<section className="ld-trackspane__sec" aria-label="Add a track">
					<AddTrack {...add} />
				</section>
			)}

			{tracksOnFile.length > 0 && (
				<section className="ld-trackspane__sec" aria-label="Tracks on file">
					<button
						type="button"
						className="ld-trackspane__disc"
						aria-expanded={tracksOnFileOpen}
						onClick={() => setFileOpen(!tracksOnFileOpen)}>
						<i className={`fa-sharp fa-solid ${tracksOnFileOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true" />
						Tracks on file · {onFileCount}
					</button>
					{tracksOnFileOpen && (
						<TracksOnFile
							tracks={tracksOnFile}
							player={player}
							hideHeading
							edit={edit ? { docs: edit.allDocs, uid: edit.uid, onChanged: edit.onChanged } : null}
						/>
					)}
				</section>
			)}
		</div>
	);
};

export default TracksPane;
